import { Button, Tooltip } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import ToggleOffIcon from "@material-ui/icons/ToggleOff";
import ToggleOnIcon from "@material-ui/icons/ToggleOn";
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import { SERVER_URL } from "../../../../api";
import {
	changeStatusStructure,
	deleteStructure,
	getStructures,
} from "../../../../api/structure";
import { formatFloat, formatInteger, getNonEmpty } from "../../../../utils/helpers";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import PreviewDialog from "../../../components/dialogs/PreviewDialog";
import Table, {
	buttonsStyle,
	dateFormatter,
} from "../../../components/tables/table";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { getDataBlocks } from "../../../../utils/structuresCalcs";

export default function StructuresPage() {
	const [data, setData] = useState([]);
	const [selectedStructure, setSelectedStructure] = useState(null);
	const [openConfirmDialog, setOpenConfirmDialog] = useState(null);
	const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
	const [previewImage, setPreviewImage] = useState(null);
	const [refresh, setRefresh] = useState(false);
	const history = useHistory();
	const user = useSelector(
		(store) => store.authentication?.user,
		shallowEqual
	);

	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		getStructures()
			.then((res) => {
				if (res.status === 200) {
					getData(res.data);
					setRefresh(false);
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage: "Could not get structures.",
				});
			});
	}, [refresh]);

	async function getData(structures) {
		let data = [];
		for (let i = 0; i < structures.length; ++i) {
			const elem = {};
			const actS = structures[i];
			elem.fullName = getNonEmpty(actS.fullName);
			elem.createdAt = actS.createdAt;
			elem.id = actS._id;
			elem.active = actS.active;
			
			const calcs = await getDataBlocks(actS?.blocks || [], null);
			elem.kcals = calcs?.kcals;
			elem.carbohydrates = calcs?.carbohydrates;
			elem.proteins = calcs?.proteins;
			elem.fats = calcs?.fats;
			elem.fiber = calcs?.fiber;

			data = data.concat(elem);
		}
		setData(data);
		disableLoadingData();
	}

	function buttonFormatter(cell) {
		const elem = data.find((item) => item.id === cell);
		return (
			<>
				<Tooltip title="Edit">
					<Button
						style={buttonsStyle}
						size="small"
						onClick={() =>
							history.push("/edit-structure/" + cell)
						}
					>
						<EditIcon />
					</Button>
				</Tooltip>
				{user?.role.includes("admin") && (
					<>
						<Tooltip title={elem?.active ? "Disable" : "Enable"}>
							<Button
								style={buttonsStyle}
								size="small"
								onClick={() => {
									setSelectedStructure(elem);
									setOpenConfirmDialog(1);
								}}
							>
								{elem?.active ? (
									<ToggleOffIcon />
								) : (
									<ToggleOnIcon style={{ color: "#00F" }} />
								)}
							</Button>
						</Tooltip>
						<Tooltip title="Delete">
							<Button
								style={buttonsStyle}
								size="small"
								onClick={() => {
									setSelectedStructure(cell);
									setOpenConfirmDialog(2);
								}}
							>
								<DeleteIcon />
							</Button>
						</Tooltip>
					</>
				)}
			</>
		);
	}

	const numbersHeaderStlye = { width: '80px' };

	const columns = [
		{ 
			dataField: "fullName", 
			text: "Full Name", 
			// headerStyle: { width: '500px' },
			sort: true 
		},
		{
			dataField: "kcals",
			text: "kcal",
			headerAlign: 'center',
			align: 'center',
			headerStyle: numbersHeaderStlye,
			formatter: formatInteger
		},
		{
			dataField: "carbohydrates",
			text: "cho",
			headerAlign: 'center',
			align: 'center',
			headerStyle: numbersHeaderStlye,
			formatter: formatInteger
		},
		{
			dataField: "proteins",
			text: "pro",
			headerAlign: 'center',
			align: 'center',
			headerStyle: numbersHeaderStlye,
			formatter: formatInteger
		},
		{
			dataField: "fats",
			text: "fat",
			headerAlign: 'center',
			align: 'center',
			headerStyle: numbersHeaderStlye,
			formatter: formatInteger
		},
		{
			dataField: "fiber",
			text: "fiber",
			headerStyle: numbersHeaderStlye,
			headerAlign: 'center',
			align: 'center',
			formatter: formatInteger,
		},
		{ 
			dataField: "id", 
			text: "", 
			headerStyle: {width: '150px'},
			formatter: buttonFormatter 
		},
	];

	if (isLoadingData) return (<ContentSkeleton />)
	return (
		<>
			<Card>
				<CardHeader title="Structures list">
					<CardHeaderToolbar>
						<button
							type="button"
							className="btn btn-primary"
							onClick={() => history.push("/edit-structure")}
						>
							Add new
						</button>
					</CardHeaderToolbar>
				</CardHeader>
				<CardBody>
					<Table data={data} columns={columns} />
					<ConfirmDialog
						title={`Are you sure you want to ${
							selectedStructure?.active ? "disable" : "enable"
						} this structure?`}
						open={openConfirmDialog === 1}
						setOpen={setOpenConfirmDialog}
						onConfirm={() => {
							changeStatusStructure(
								selectedStructure.id,
								!selectedStructure?.active
							)
								.then((res) => {
									if (res.status === 200) {
										alertSuccess({
											title: `${
												selectedStructure?.active
													? "Disabled!"
													: "Enabled!"
											}`,
											customMessage: `Structure ${
												selectedStructure?.active
													? "disabled"
													: "enabled"
											} successfully`,
										});
										setRefresh(true);
									}
								})
								.catch((error) => {
									alertError({
										error: error,
										customMessage: `Could not ${
											selectedStructure?.active
												? "disable"
												: "enable"
										} structure.`,
									});
								});
						}}
					/>
					<ConfirmDialog
						title={
							"Are you sure you want to remove this structure?"
						}
						open={openConfirmDialog === 2}
						setOpen={setOpenConfirmDialog}
						onConfirm={() => {
							deleteStructure(selectedStructure)
								.then((res) => {
									if (
										res.status === 204 ||
										res.status === 200
									) {
										alertSuccess({
											title: "Deleted!",
											customMessage:
												"Structure removed successfully.",
										});
										setRefresh(true);
									}
								})
								.catch((error) => {
									alertError({
										error: error,
										customMessage:
											"Could not remove structure.",
									});
								});
						}}
					/>
					<PreviewDialog
						title={"Preview image"}
						open={openPreviewDialog}
						setOpen={setOpenPreviewDialog}
						src={
							previewImage
								? `${SERVER_URL}/${previewImage}`
								: null
						}
					/>
				</CardBody>
			</Card>
		</>
	);
}
